import React, { useState, useEffect, useRef } from "react";
import { TextField, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  dateFieldRoot: {
    "& .MuiInputBase-input": {
      color: theme.palette?.type === "dark" ? "#ffffff !important" : "var(--hrms-text-strong, #1f2937) !important",
      "-webkit-text-fill-color": theme.palette?.type === "dark" ? "#ffffff !important" : "var(--hrms-text-strong, #1f2937) !important",
    },
    "& .MuiInputBase-input::placeholder": {
      opacity: "1 !important",
      color: theme.palette?.type === "dark" ? "rgba(255, 255, 255, 0.7) !important" : "var(--hrms-text-strong, #1f2937) !important",
      "-webkit-text-fill-color": theme.palette?.type === "dark" ? "rgba(255, 255, 255, 0.7) !important" : "var(--hrms-text-strong, #1f2937) !important",
      fontFamily: "inherit !important",
      fontSize: "1rem !important",
      fontWeight: "400 !important",
    },
    "& .MuiInputBase-input::-webkit-input-placeholder": {
      opacity: "1 !important",
      color: theme.palette?.type === "dark" ? "rgba(255, 255, 255, 0.7) !important" : "var(--hrms-text-strong, #1f2937) !important",
      "-webkit-text-fill-color": theme.palette?.type === "dark" ? "rgba(255, 255, 255, 0.7) !important" : "var(--hrms-text-strong, #1f2937) !important",
      fontFamily: "inherit !important",
      fontSize: "1rem !important",
      fontWeight: "400 !important",
    },
  },
}));

const DdMmYyyyDateField = ({
  id,
  name,
  label,
  value,
  placeholder,
  onChange,
  error,
  helperText,
  min,
  max,
  fullWidth = true,
  size = "small",
  variant = "outlined",
  disabled = false,
  required = false,
  className = "",
  style = {},
  InputLabelProps = {},
  InputProps = {},
  inputProps = {},
}) => {
  const classes = useStyles();
  const hiddenInputRef = useRef(null);
  const isFocusedRef = useRef(false);

  const getDisplayValue = (val) => {
    if (!val) return "";
    const rawVal = typeof val === "object" && val !== null && val.target ? val.target.value : val;
    if (!rawVal) return "";
    const m = moment(rawVal, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", "MM/DD/YYYY"], true);
    if (m.isValid()) {
      return m.format("DD/MM/YYYY");
    }
    const parsed = moment(rawVal, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", "MM/DD/YYYY"]);
    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY");
    }
    return String(rawVal);
  };

  const getYmdValue = (val) => {
    if (!val) return "";
    const rawVal = typeof val === "object" && val !== null && val.target ? val.target.value : val;
    if (!rawVal) return "";
    const m = moment(rawVal, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", "MM/DD/YYYY"], true);
    if (m.isValid()) {
      return m.format("YYYY-MM-DD");
    }
    const parsed = moment(rawVal, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", "MM/DD/YYYY"]);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DD");
    }
    return "";
  };

  const [inputText, setInputText] = useState(() => getDisplayValue(value));

  useEffect(() => {
    if (!isFocusedRef.current) {
      setInputText(getDisplayValue(value));
    }
  }, [value]);

  const formatInput = (raw) => {
    let digits = raw.replace(/\D/g, "");

    // Validate Day
    if (digits.length >= 1) {
      const d1 = parseInt(digits[0], 10);
      if (d1 > 3) {
        digits = `0${d1}` + digits.slice(1);
      }
    }
    if (digits.length >= 2) {
      let day = parseInt(digits.slice(0, 2), 10);
      if (day === 0) day = 1;
      if (day > 31) day = 31;
      digits = String(day).padStart(2, "0") + digits.slice(2);
    }

    // Validate Month
    if (digits.length >= 3) {
      const m1 = parseInt(digits[2], 10);
      if (m1 > 1) {
        digits = digits.slice(0, 2) + `0${m1}` + digits.slice(3);
      }
    }
    if (digits.length >= 4) {
      let month = parseInt(digits.slice(2, 4), 10);
      if (month === 0) month = 1;
      if (month > 12) month = 12;
      digits = digits.slice(0, 2) + String(month).padStart(2, "0") + digits.slice(4);
    }

    if (digits.length > 8) {
      digits = digits.slice(0, 8);
    }

    if (digits.length >= 5) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length >= 3) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else if (digits.length === 2 && !raw.endsWith("/")) {
      return `${digits}/`;
    }
    return digits;
  };

  const handleTextChange = (e) => {
    const raw = e.target.value;
    const isBackspace = e.nativeEvent && e.nativeEvent.inputType === "deleteContentBackward";

    if (isBackspace) {
      let cleaned = raw;
      if (cleaned.endsWith("/")) {
        cleaned = cleaned.slice(0, -1);
      }
      setInputText(cleaned);
      if (typeof onChange === "function") {
        onChange({ target: { name: name || id, value: "" } });
      }
      return;
    }

    const formatted = formatInput(raw);
    setInputText(formatted);

    if (typeof onChange === "function") {
      if (formatted.length === 10) {
        const m = moment(formatted, "DD/MM/YYYY", true);
        if (m.isValid()) {
          onChange({
            target: { name: name || id, value: m.format("YYYY-MM-DD") },
          });
        } else {
          onChange({
            target: { name: name || id, value: formatted },
          });
        }
      } else {
        onChange({
          target: { name: name || id, value: formatted },
        });
      }
    }
  };

  const handleNativePickerChange = (e) => {
    const ymd = e.target.value;
    if (ymd) {
      const m = moment(ymd, "YYYY-MM-DD", true);
      if (m.isValid()) {
        setInputText(m.format("DD/MM/YYYY"));
        if (typeof onChange === "function") {
          const syntheticEvent = {
            target: { name: name || id, value: m.format("YYYY-MM-DD") },
          };
          onChange(syntheticEvent);
        }
      }
    }
  };

  const handleOpenPicker = (e) => {
    if (e) e.stopPropagation();
    if (hiddenInputRef.current) {
      if (typeof hiddenInputRef.current.showPicker === "function") {
        hiddenInputRef.current.showPicker();
      } else {
        hiddenInputRef.current.click();
      }
    }
  };

  const isShrunk =
    InputLabelProps && InputLabelProps.shrink !== undefined
      ? InputLabelProps.shrink
      : (label ? true : Boolean(inputText));

  const effectivePlaceholder =
    placeholder !== undefined
      ? placeholder
      : "DD/MM/YYYY";

  return (
    <div style={{ position: "relative", width: fullWidth ? "100%" : "auto", ...style }}>
      <TextField
        fullWidth={fullWidth}
        id={id}
        name={name}
        label={label}
        variant={variant}
        size={size}
        disabled={disabled}
        required={required}
        className={`${classes.dateFieldRoot} dd-mm-yyyy-date-field ${className}`}
        value={inputText}
        placeholder={effectivePlaceholder}
        onChange={handleTextChange}
        onFocus={() => {
          isFocusedRef.current = true;
        }}
        onBlur={() => {
          isFocusedRef.current = false;
        }}
        error={error}
        helperText={helperText}
        InputLabelProps={{
          shrink: isShrunk,
          ...InputLabelProps,
        }}
        inputProps={{
          ...inputProps,
        }}
        InputProps={{
          endAdornment: (
            <IconButton
              size="small"
              onClick={handleOpenPicker}
              style={{ padding: 4 }}
              tabIndex={-1}
              aria-label="Open calendar"
            >
              <CalendarTodayIcon fontSize="small" style={{ color: "#036dda" }} />
            </IconButton>
          ),
          ...InputProps,
        }}
      />
      <input
        ref={hiddenInputRef}
        type="date"
        value={getYmdValue(value)}
        min={getYmdValue(min)}
        max={getYmdValue(max)}
        onChange={handleNativePickerChange}
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: "100%",
          height: "1px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
        tabIndex={-1}
      />
    </div>
  );
};

export default DdMmYyyyDateField;
